// Renderer / view layer. Owns all Pixi display objects and syncs them from the World
// each frame (plan §4.2). Entities are drawn with hand-authored pixel-art textures
// (render/pixelArt.ts); positions interpolate between sim ticks. Tinted categories
// (projectiles/orbs/shots/particles/aura) reuse a white sprite colored per element.

import { Application, Container, Graphics, Sprite, TilingSprite } from 'pixi.js';
import type { Texture } from 'pixi.js';
import { clamp, lerp } from '../core/math';
import { Rng } from '../core/rng';
import { ALTAR, ARENA, MOTE, PLAYER, ZOOM } from '../game/balance';
import { VARIANT } from '../game/variant';
import type { VineWall, World } from '../game/world';
import type { AbilitySystemHidden } from '../game/abilitiesHidden';
import { AbilitySystem } from '../game/abilities';
import type { Beam } from '../game/fx';
import type { Boulder } from '../game/boulders';
import type { Puddle } from '../game/puddles';
import { buildArt } from './pixelArt';
import type { Art } from './pixelArt';

const MAX_SHAKE = 7; // peak screen-shake offset in world px
const CRIT_COLOR = 0xffd34d;
const HIT_COLOR = 0xffffff;
const WALK_STRIDE = 7; // px of travel per leg swap
const FROST_TINT = 0x9fd4ff; // additive icy glow on slowed enemies
const BURN_TINT = 0xff7a2a; // additive ember glow on burning enemies
const DRIP_RATE = 1.5; // droplet fall cycles per second (wet entities)
const DRIP_FALL = 9; // px a droplet falls before it resets
const DROP_SCALE = 1.9;

export class Stage {
  readonly root = new Container();
  private readonly worldLayer = new Container();
  private readonly grassLayer = new Container();
  private readonly puddleLayer = new Container();
  private readonly boulderLayer = new Container();
  private readonly vineLayer = new Container();
  private readonly catalystLayer = new Container();
  private readonly railLayer = new Container();
  private readonly arenaAbilityLayer = new Container();
  private readonly auraLayer = new Container();
  private readonly moteLayer = new Container();
  private readonly enemyLayer = new Container();
  private readonly statusLayer = new Container();
  private readonly projectileLayer = new Container();
  private readonly orbitLayer = new Container();
  private readonly particleLayer = new Container();
  private readonly beamLayer = new Container();
  private readonly dropletLayer = new Container();
  private readonly numbersLayer = new Container();
  private readonly beamGraphics = new Graphics();
  private readonly ringGraphics = new Graphics();
  private readonly player: Sprite;
  private readonly auraSprite: Sprite;
  private readonly playerGlow: Sprite;
  private readonly altar: Sprite;
  private readonly altarGlow: Sprite;
  private readonly vignette: Sprite;
  private readonly floor: TilingSprite;

  private readonly art: Art;
  private readonly enemySprites: Sprite[] = [];
  private readonly statusSprites: Sprite[] = [];
  private readonly projectileSprites: Sprite[] = [];
  private readonly enemyShotSprites: Sprite[] = [];
  private readonly moteSprites: Sprite[] = [];
  private readonly orbitSprites: Sprite[] = [];
  private readonly auraCloudSprites: Sprite[] = [];
  private readonly particleSprites: Sprite[] = [];
  private readonly beamSymbolSprites: Sprite[] = [];
  private readonly arenaVisualSprites: Sprite[] = [];
  private readonly digitSprites: Sprite[] = [];
  private readonly boulderSprites: Sprite[] = [];
  private lastBoulders: Boulder[] | null = null;
  private readonly tempBoulderSprites: Sprite[] = []; // hidden Maindan trap-ring
  private readonly storkSprites: Sprite[] = []; // hidden Berze Zeci
  private readonly noteSprites: Sprite[] = []; // hidden Trans Mute silence
  private readonly bhSprite: Sprite; // hidden Back Hole marker
  private readonly puddleSprites: Sprite[] = [];
  private lastPuddles: Puddle[] | null = null;
  private readonly vineSprites: Sprite[] = [];
  private readonly vineTelegraph = new Graphics();
  private readonly railGraphics = new Graphics();
  private readonly arenaAbilityGraphics = new Graphics();
  private readonly arenaAbilitySprites: Sprite[] = [];
  private readonly activeAbilitySprites: Sprite[] = [];
  private readonly railPylonSprites: Sprite[] = [];
  private readonly railArrowSprites: Sprite[] = [];
  private readonly nectarSprites: Sprite[] = [];
  private readonly healthOrbSprites: Sprite[] = [];
  private readonly catalystSprites: Sprite[] = [];
  private terrainLevelId = '';
  private readonly dropletSprites: Sprite[] = [];
  private prevRenderX = 0;
  private prevRenderY = 0;
  private walkPhase = 0;
  private zoom = ZOOM;

  constructor(private readonly app: Application) {
    this.art = buildArt(app.renderer);

    this.worldLayer.scale.set(this.zoom);
    this.root.addChild(this.worldLayer);

    // Tiled organic stone-ground floor + boulders (above floor, below entities).
    this.floor = new TilingSprite({
      texture: this.art.stone,
      width: ARENA.width,
      height: ARENA.height,
    });
    this.worldLayer.addChild(this.floor);
    this.worldLayer.addChild(this.grassLayer); // cosmetic grass, lowest decoration
    this.scatterFoliage('grass');
    this.worldLayer.addChild(this.puddleLayer); // water below boulders + entities
    this.worldLayer.addChild(this.boulderLayer);
    this.vineLayer.addChild(this.vineTelegraph);
    this.worldLayer.addChild(this.vineLayer);
    this.worldLayer.addChild(this.catalystLayer);
    this.railLayer.addChild(this.railGraphics);
    this.worldLayer.addChild(this.railLayer);
    this.arenaAbilityLayer.addChild(this.arenaAbilityGraphics);
    this.worldLayer.addChild(this.arenaAbilityLayer);

    // Healing altar (static): a pulsing glow under the dais; both below the entities.
    this.altarGlow = new Sprite(this.art.textures.disc);
    this.altarGlow.anchor.set(0.5);
    this.altarGlow.position.set(ALTAR.x, ALTAR.y);
    this.altarGlow.blendMode = 'add';
    this.altarGlow.tint = 0x59ffd0;
    this.worldLayer.addChild(this.altarGlow);
    this.altar = new Sprite(this.art.textures.altar);
    this.altar.anchor.set(0.5);
    this.altar.position.set(ALTAR.x, ALTAR.y);
    this.altar.scale.set((ALTAR.radius * 2 * 1.35) / this.art.textures.altar.width);
    this.worldLayer.addChild(this.altar);

    // Draw order (bottom → top).
    this.worldLayer.addChild(this.auraLayer);
    this.worldLayer.addChild(this.moteLayer);
    this.worldLayer.addChild(this.enemyLayer);
    this.worldLayer.addChild(this.statusLayer);
    this.worldLayer.addChild(this.projectileLayer);
    this.worldLayer.addChild(this.orbitLayer);
    this.worldLayer.addChild(this.particleLayer);
    this.beamLayer.addChild(this.beamGraphics);
    this.beamLayer.addChild(this.ringGraphics);
    this.worldLayer.addChild(this.beamLayer);

    this.auraSprite = new Sprite(this.art.textures.disc);
    this.auraSprite.anchor.set(0.5);
    this.auraSprite.alpha = 0.18;
    this.auraSprite.visible = false;
    this.auraLayer.addChild(this.auraSprite);

    // Hidden Back Hole marker sphere (dark disc), under the enemies so they sink into it.
    this.bhSprite = new Sprite(this.art.textures.disc);
    this.bhSprite.anchor.set(0.5);
    this.bhSprite.visible = false;
    this.auraLayer.addChild(this.bhSprite);

    // Ability buff aura, drawn under the player (additive glow).
    this.playerGlow = new Sprite(this.art.textures.disc);
    this.playerGlow.anchor.set(0.5);
    this.playerGlow.visible = false;
    this.playerGlow.blendMode = 'add';
    this.worldLayer.addChild(this.playerGlow);

    this.player = new Sprite(this.art.textures.player);
    this.player.anchor.set(0.5);
    this.worldLayer.addChild(this.player);
    this.worldLayer.addChild(this.dropletLayer); // drip animation above entities
    this.worldLayer.addChild(this.numbersLayer); // on top of everything

    // Screen-space vignette over the world (sized to the viewport each frame).
    this.vignette = new Sprite(this.art.vignette);
    this.root.addChild(this.vignette);
  }

  // Scatter seeded, low-contrast floor props. Expansion arenas use rooted cellar growth
  // or observatory debris rather than abstract marks painted over the playfield.
  private scatterFoliage(foliage: 'grass' | 'roots' | 'forge'): void {
    this.grassLayer.removeChildren();
    const rng = new Rng(foliage === 'grass' ? 4242 : foliage === 'roots' ? 7431 : 9812);
    const variants =
      foliage === 'grass'
        ? ['grass0', 'grass1', 'grass2']
        : foliage === 'roots'
          ? ['root0', 'root1', 'root2']
          : ['forge0', 'forge1', 'forge2'];
    const count = foliage === 'grass' ? 96 : foliage === 'roots' ? 28 : 28;
    for (let i = 0; i < count; i++) {
      const tex = this.art.textures[variants[rng.int(0, variants.length)]];
      const s = new Sprite(tex);
      s.anchor.set(0.5, 0.9); // pivot near the base
      s.x = rng.range(0, ARENA.width);
      s.y = rng.range(0, ARENA.height);
      const sc = foliage === 'forge' ? rng.range(1.5, 2.2) : rng.range(1.8, 2.8);
      s.scale.set(rng.next() < 0.5 ? -sc : sc, sc); // random horizontal flip for variety
      s.alpha =
        foliage === 'grass'
          ? rng.range(0.28, 0.36)
          : foliage === 'roots'
            ? rng.range(0.28, 0.4)
            : rng.range(0.34, 0.5);
      this.grassLayer.addChild(s);
    }
  }

  sync(world: World, alpha: number): void {
    this.syncTerrainTheme(world);
    const p = world.player;
    const rx = lerp(p.prevX, p.x, alpha);
    const ry = lerp(p.prevY, p.y, alpha);

    // Walk animation: swap legs every WALK_STRIDE px traveled; idle when still; tiny bob.
    const moved = Math.hypot(rx - this.prevRenderX, ry - this.prevRenderY);
    this.prevRenderX = rx;
    this.prevRenderY = ry;
    // Arenas may reskin the hero (NinjaKid Dojo); missing frames fall back to the alchemist.
    const skin = world.levelDefinition.visualTheme.playerSpritePrefix ?? 'player';
    let frame = this.art.textures[skin] ?? this.art.textures.player;
    let bob = 0;
    if (moved > 0.05) {
      this.walkPhase += moved;
      const step = Math.floor(this.walkPhase / WALK_STRIDE);
      frame =
        step % 2 === 0
          ? (this.art.textures[`${skin}A`] ?? this.art.textures.playerA)
          : (this.art.textures[`${skin}B`] ?? this.art.textures.playerB);
      bob = step % 2 === 0 ? 0 : 1;
    } else {
      this.walkPhase = 0;
    }
    // Hidden OverLord doubles the player's render size while active.
    const sizeMul = world.usesHiddenAbilitySystem
      ? (world.abilities as AbilitySystemHidden).sizeMul
      : 1;
    this.player.texture = frame;
    this.player.position.set(rx, ry - bob);
    fit(this.player, PLAYER.radius * sizeMul);

    // Ability buff glow (Berserk red / Aegis gold / Tempest cyan), pulsing.
    const glow = world.abilities.glowColor;
    if (glow >= 0 && world.levelDefinition.id === 'base_lab') {
      const pulse = 1 + 0.12 * Math.sin(world.elapsedSec * 12);
      this.playerGlow.visible = true;
      this.playerGlow.position.set(rx, ry);
      this.playerGlow.scale.set(((PLAYER.radius * 3.4) / this.art.textures.disc.height) * pulse);
      this.playerGlow.tint = glow;
      this.playerGlow.alpha = 0.45;
    } else {
      this.playerGlow.visible = false;
    }

    const ws = world.weapons;

    // Boulders (static; sprites rebuilt only when the run's layout changes).
    if (world.boulders !== this.lastBoulders) {
      this.boulderLayer.removeChildren();
      this.boulderSprites.length = 0;
      const btex =
        this.art.textures[world.levelDefinition.visualTheme.obstacleTexture] ??
        this.art.textures.boulder;
      const mechanicalObstacle = world.levelDefinition.visualTheme.obstacleTexture !== 'boulder';
      for (const b of world.boulders) {
        const s = new Sprite(btex);
        s.anchor.set(0.5, mechanicalObstacle ? 0.5 : 0.62);
        s.x = b.x;
        s.y = b.y;
        s.scale.set((b.radius * 2) / btex.width);
        this.boulderLayer.addChild(s);
        this.boulderSprites.push(s);
      }
      this.lastBoulders = world.boulders;
    }

    // Temporary boulder ring (hidden Maindan). Empty on the base game, so this loop no-ops.
    const tb = world.tempBoulders;
    const btex = this.art.textures.boulder;
    for (let i = 0; i < tb.length; i++) {
      const b = tb[i];
      const s = reuse(this.tempBoulderSprites, this.boulderLayer, i, btex);
      s.anchor.set(0.5, 0.62);
      s.x = b.x;
      s.y = b.y;
      s.scale.set((b.radius * 2) / btex.width);
      s.visible = true;
    }
    hideFrom(this.tempBoulderSprites, tb.length);

    // Puddles (static; translucent water; rebuilt only when the run's layout changes).
    if (world.puddles !== this.lastPuddles) {
      this.puddleLayer.removeChildren();
      this.puddleSprites.length = 0;
      for (const pd of world.puddles) {
        const prefix = world.levelDefinition.visualTheme.puddleTexturePrefix;
        const ptex = this.art.textures[`${prefix}${pd.shape}`] ?? this.art.textures.puddle0;
        const s = new Sprite(ptex);
        s.anchor.set(0.5);
        s.x = pd.x;
        s.y = pd.y;
        // scale by the smaller dimension so the visible water always covers the wet-trigger circle
        s.scale.set((pd.radius * 2) / Math.min(ptex.width, ptex.height));
        s.alpha = 0.5; // translucent so the ground shows through
        this.puddleLayer.addChild(s);
        this.puddleSprites.push(s);
      }
      this.lastPuddles = world.puddles;
    }

    // Healing altar: full color + pulsing glow when ready, dimmed while on cooldown.
    const altarReady = world.altarCd <= 0;
    this.altar.alpha = altarReady ? 1 : 0.55;
    this.altar.tint = altarReady ? 0xffffff : 0x8a90a8;
    this.altarGlow.visible = altarReady;
    if (altarReady) {
      const pulse = 1 + 0.15 * Math.sin(world.elapsedSec * 4);
      this.altarGlow.scale.set(((ALTAR.radius * 3.2) / this.art.textures.disc.height) * pulse);
      this.altarGlow.alpha = 0.4;
    }

    // Base Lab keeps its legacy soft disc. Expansion aura upgrades use the same
    // breathing pixel-cloud compositions as the SpriteKit game.
    if (ws.auraActive) {
      if (world.levelDefinition.id === 'base_lab') {
        this.auraSprite.visible = true;
        this.auraSprite.position.set(rx, ry);
        this.auraSprite.scale.set((ws.auraRadius * 2) / this.art.textures.disc.height);
        this.auraSprite.tint = ws.auraColor;
        hideFrom(this.auraCloudSprites, 0);
      } else {
        this.auraSprite.visible = false;
        this.syncExpansionAura(world, rx, ry);
      }
    } else {
      this.auraSprite.visible = false;
      hideFrom(this.auraCloudSprites, 0);
    }

    // Enemies (per-type texture; scale-pop on hit; additive frost/burn overlay on status).
    const en = world.enemies;
    let statusN = 0;
    let dropN = 0;
    for (let i = 0; i < en.count; i++) {
      const e = en.items[i];
      const tex = this.art.textures[e.sprite] ?? this.art.textures.slime;
      const ex = lerp(e.prevX, e.x, alpha);
      const ey = lerp(e.prevY, e.y, alpha);
      const sc = ((e.radius * 2) / tex.height) * (e.hitFlashSec > 0 ? 1.18 : 1);
      const s = reuse(this.enemySprites, this.enemyLayer, i, tex);
      s.texture = tex;
      s.x = ex;
      s.y = ey;
      s.scale.set(sc);
      s.tint = HIT_COLOR;
      s.visible = true;

      if (e.slowSec > 0 || e.burnSec > 0 || e.sporeMarkedSec > 0) {
        const ov = reuse(this.statusSprites, this.statusLayer, statusN++, tex);
        ov.texture = tex;
        ov.x = ex;
        ov.y = ey;
        ov.scale.set(sc);
        ov.tint =
          e.slowSec > 0
            ? e.slowTint || FROST_TINT
            : e.sporeMarkedSec > 0
              ? 0xf35fca
              : e.burnTint || BURN_TINT;
        ov.blendMode = 'add';
        ov.alpha = 0.55;
        ov.visible = true;
      }

      // Dripping droplets while wet (slowed by a puddle): two staggered drips.
      if (e.wetSec > 0) {
        dropN = this.emitDrips(
          dropN,
          ex,
          ey + e.radius * 0.2,
          e.radius * 0.45,
          world.elapsedSec + i * 0.13,
        );
      }
    }
    hideFrom(this.enemySprites, en.count);
    hideFrom(this.statusSprites, statusN);

    // Player drip while wet.
    if (p.wetSec > 0) {
      dropN = this.emitDrips(
        dropN,
        rx,
        ry + PLAYER.radius * 0.2,
        PLAYER.radius * 0.45,
        world.elapsedSec,
      );
    }
    hideFrom(this.dropletSprites, dropN);

    // Temporary Bloom Vines. The faint mint ring is visible before the wall becomes
    // solid, while the collision system receives only the activated subset from World.
    this.syncVines(world.vineWalls);
    this.syncCatalystPools(world);
    this.syncGravityRails(world);
    this.syncArenaAbilities(world, rx, ry);

    // Projectiles (white sprite tinted by element).
    const pr = world.projectiles;
    for (let i = 0; i < pr.count; i++) {
      const b = pr.items[i];
      const tex = this.art.textures[b.sprite] ?? this.art.textures.proj;
      const s = reuse(this.projectileSprites, this.projectileLayer, i, tex);
      s.texture = tex;
      s.x = lerp(b.prevX, b.x, alpha);
      s.y = lerp(b.prevY, b.y, alpha);
      const diameter = Math.max(13, b.radius * 2.8);
      if (b.sprite === 'projectileComet') {
        setSpriteSize(s, Math.max(17, b.radius * 3.5), Math.max(10, b.radius * 2.4));
      } else if (b.sprite === 'prismBolt') {
        setSpriteSize(s, Math.max(13, b.radius * 3.4), Math.max(7, b.radius * 1.7));
      } else if (b.sprite === 'proj') {
        setSpriteSize(s, Math.max(13, b.radius * 3.4), Math.max(7, b.radius * 1.7));
      } else {
        setSpriteSize(s, diameter, diameter);
      }
      s.rotation = Math.atan2(b.vy, b.vx); // point the bolt along its travel direction
      s.tint = b.color;
      s.visible = true;
    }
    hideFrom(this.projectileSprites, pr.count);

    // Enemy shots.
    const es = world.enemyShots;
    for (let i = 0; i < es.count; i++) {
      const b = es.items[i];
      const tex = this.art.textures[b.sprite] ?? this.art.textures.shot;
      const s = reuse(this.enemyShotSprites, this.projectileLayer, i, tex);
      s.texture = tex;
      s.x = lerp(b.prevX, b.x, alpha);
      s.y = lerp(b.prevY, b.y, alpha);
      s.scale.set((b.radius * 2.2) / tex.height);
      s.tint = b.color;
      s.visible = true;
    }
    hideFrom(this.enemyShotSprites, es.count);

    // Motes.
    const mo = world.motes;
    for (let i = 0; i < mo.count; i++) {
      const m = mo.items[i];
      const s = reuse(this.moteSprites, this.moteLayer, i, this.art.textures.mote);
      s.x = m.x;
      s.y = m.y;
      s.scale.set((MOTE.radius * 1.8) / this.art.textures.mote.height);
      s.tint = HIT_COLOR;
      s.visible = true;
    }
    hideFrom(this.moteSprites, mo.count);

    // Rootling nectar is intentionally above terrain and motes, so the amber recovery
    // choice stays readable during a busy bloom pulse.
    const ne = world.nectars;
    for (let i = 0; i < ne.count; i++) {
      const nectar = ne.items[i];
      const s = reuse(this.nectarSprites, this.moteLayer, i, this.art.textures.nectar);
      s.texture = this.art.textures.nectar;
      s.x = nectar.x;
      s.y = nectar.y;
      const pulse = 1 + 0.1 * Math.sin((8 - nectar.lifeSec) * 8);
      s.scale.set(((PLAYER.radius * 1.35) / this.art.textures.nectar.height) * pulse);
      s.tint = HIT_COLOR;
      s.alpha = Math.min(1, nectar.lifeSec * 1.4);
      s.visible = true;
    }
    hideFrom(this.nectarSprites, ne.count);

    // Starfall Easy recovery orbs pulse above the floor and remain distinct from XP.
    const healthOrbs = world.healthOrbs;
    for (let i = 0; i < healthOrbs.count; i++) {
      const orb = healthOrbs.items[i];
      const s = reuse(
        this.healthOrbSprites,
        this.moteLayer,
        i,
        this.art.textures.healthOrb,
      );
      s.texture = this.art.textures.healthOrb;
      s.x = orb.x;
      const phase = (12 - orb.lifeSec) * 8;
      s.y = orb.y + Math.sin(phase * 0.5) * 3;
      const pulse = 1 + 0.12 * Math.sin(phase);
      s.scale.set(
        ((PLAYER.radius * 2) / this.art.textures.healthOrb.height) * pulse,
      );
      s.tint = HIT_COLOR;
      s.alpha = Math.min(1, orb.lifeSec * 1.5);
      s.visible = true;
    }
    hideFrom(this.healthOrbSprites, healthOrbs.count);

    // Orbit upgrades retain their own native silhouettes instead of sharing one orb.
    let orbCount = 0;
    if (ws.orbitActive) {
      if (world.levelDefinition.id === 'base_lab') {
        for (let k = 0; k < ws.orbitCount; k++) {
          const angle = ws.orbitSpin + (k / ws.orbitCount) * Math.PI * 2;
          const sprite = reuse(
            this.orbitSprites,
            this.orbitLayer,
            orbCount++,
            this.art.textures.orb,
          );
          sprite.texture = this.art.textures.orb;
          sprite.x = rx + Math.cos(angle) * ws.orbitRingRadius;
          sprite.y = ry + Math.sin(angle) * ws.orbitRingRadius;
          setSpriteSize(sprite, ws.orbitOrbRadius * 2, ws.orbitOrbRadius * 2);
          sprite.rotation = 0;
          sprite.tint = ws.orbitColor;
          sprite.alpha = 1;
          sprite.visible = true;
        }
      } else {
        for (const orbit of ws.orbitVisuals) {
          const visual = orbitWeaponAppearance(orbit.weaponKey);
          const texture = this.art.textures[visual.texture] ?? this.art.textures.orb;
          for (let k = 0; k < orbit.count; k++) {
            const angle = orbit.spin + (k / orbit.count) * Math.PI * 2;
            const sprite = reuse(this.orbitSprites, this.orbitLayer, orbCount++, texture);
            sprite.texture = texture;
            sprite.x = rx + Math.cos(angle) * orbit.ringRadius;
            sprite.y = ry + Math.sin(angle) * orbit.ringRadius;
            setSpriteSize(
              sprite,
              orbit.orbRadius * 2 * visual.aspect,
              orbit.orbRadius * 2,
            );
            sprite.rotation = angle;
            sprite.tint = orbit.color;
            sprite.alpha = 1;
            sprite.visible = true;
          }
        }
      }
    }
    hideFrom(this.orbitSprites, orbCount);

    // Hidden-variant ability visuals (storks, Back Hole sphere, silence notes).
    if (world.usesHiddenAbilitySystem) this.syncHiddenFx(world, rx, ry);

    // Death particles.
    const pc = world.particles;
    for (let i = 0; i < pc.count; i++) {
      const q = pc.items[i];
      const s = reuse(this.particleSprites, this.particleLayer, i, this.art.textures.disc);
      s.x = q.x;
      s.y = q.y;
      s.scale.set(6 / this.art.textures.disc.height);
      s.tint = q.color;
      s.alpha = q.life > 0 ? q.life / q.maxLife : 0;
      s.visible = true;
    }
    hideFrom(this.particleSprites, pc.count);

    // Lightning arcs (chain weapons) - redraw jagged bolts each frame.
    const bg = this.beamGraphics;
    bg.clear();
    const bm = world.beams;
    let beamSymbolCount = 0;
    const hiddenBaseLaser = VARIANT === 'hidden' && world.levelDefinition.id === 'base_lab';
    for (let i = 0; i < bm.count; i++) {
      const b = bm.items[i];
      const beamAlpha = b.life > 0 ? b.life / b.maxLife : 0;
      if (b.kind === 'firefly' || b.kind === 'spore' || b.kind === 'solar') {
        beamSymbolCount = this.syncBeamSymbols(b, beamSymbolCount, beamAlpha);
      }
      if (b.kind === 'straight' || b.kind === 'solar' || (b.kind === 'lightning' && hiddenBaseLaser)) {
        drawStraightBeam(bg, b.x1, b.y1, b.x2, b.y2, b.color, beamAlpha);
      } else if (b.kind === 'lightning') {
        drawBolt(bg, b.x1, b.y1, b.x2, b.y2, b.color, beamAlpha);
      }
    }
    hideFrom(this.beamSymbolSprites, beamSymbolCount);

    // Ability shockwave rings - expand from 0 → maxRadius while fading.
    const rg = this.ringGraphics;
    rg.clear();
    const rn = world.rings;
    for (let i = 0; i < rn.count; i++) {
      const r = rn.items[i];
      const t = r.life > 0 ? r.life / r.maxLife : 0; // 1 → 0
      rg.circle(r.x, r.y, (1 - t) * r.maxRadius).stroke({
        color: r.color,
        width: 3,
        alpha: t * 0.9,
      });
    }

    // Floating damage numbers, drawn as pixel digits (crits larger + gold).
    const dn = world.damageNumbers;
    let digit = 0;
    for (let i = 0; i < dn.count; i++) {
      const d = dn.items[i];
      const str = `${d.value}`;
      const ds = (d.crit ? 9 : 6) / 5; // scale (digit glyphs are 5px tall)
      const advance = 4 * ds;
      const firstX = d.x - ((str.length - 1) * advance) / 2;
      const tint = d.crit ? CRIT_COLOR : HIT_COLOR;
      const alpha = d.life > 0 ? d.life / d.maxLife : 0;
      for (let c = 0; c < str.length; c++) {
        const tex = this.art.textures[`d${str[c]}`];
        const s = reuse(this.digitSprites, this.numbersLayer, digit++, tex);
        s.texture = tex;
        s.x = firstX + c * advance;
        s.y = d.y;
        s.scale.set(ds);
        s.tint = tint;
        s.alpha = alpha;
        s.visible = true;
      }
    }
    hideFrom(this.digitSprites, digit);

    // Vignette fills the viewport (screen space - doesn't scroll).
    this.vignette.width = this.app.screen.width;
    this.vignette.height = this.app.screen.height;

    this.updateCamera(rx, ry, world.shakeTrauma);
  }

  /** Update static art only when a newly-created World selects another arena. */
  private syncTerrainTheme(world: World): void {
    if (this.terrainLevelId === world.levelDefinition.id) return;
    this.terrainLevelId = world.levelDefinition.id;
    const theme = world.levelDefinition.visualTheme;
    this.floor.texture =
      theme.groundTexture === 'verdant'
        ? this.art.verdant
        : theme.groundTexture === 'conduit'
          ? this.art.conduit
          : this.art.stone;
    this.scatterFoliage(theme.foliage);
    this.altar.texture = this.art.textures[theme.altarTexture] ?? this.art.textures.altar;
    this.altar.scale.set((ALTAR.radius * 2 * 1.35) / this.altar.texture.width);
    this.altarGlow.tint =
      theme.groundTexture === 'verdant'
        ? 0xffc35a
        : theme.groundTexture === 'conduit'
          ? 0x8cf6ff
          : 0x59ffd0;
    this.lastBoulders = null;
    this.lastPuddles = null;
  }

  private syncVines(vines: readonly VineWall[]): void {
    const tex = this.art.textures.vineWall;
    const g = this.vineTelegraph;
    g.clear();
    for (let i = 0; i < vines.length; i++) {
      const wall = vines[i];
      const s = reuse(this.vineSprites, this.vineLayer, i, tex);
      s.texture = tex;
      s.x = wall.x;
      s.y = wall.y;
      s.scale.set((wall.radius * 2) / tex.height);
      const telegraph = wall.telegraphSec > 0;
      s.alpha = telegraph ? 0.42 : 1;
      s.visible = true;
      if (telegraph) {
        const pulse = 1 + 0.12 * Math.sin(wall.telegraphSec * 16);
        g.circle(wall.x, wall.y, wall.radius * 1.35 * pulse).stroke({
          color: 0xa6f2dc,
          width: 2,
          alpha: 0.8,
        });
      }
    }
    hideFrom(this.vineSprites, vines.length);
  }

  private syncCatalystPools(world: World): void {
    const tex = this.art.textures.catalystPool;
    const pools = world.catalystPools;
    for (let i = 0; i < pools.length; i++) {
      const pool = pools[i];
      const s = reuse(this.catalystSprites, this.catalystLayer, i, tex);
      s.texture = tex;
      s.x = pool.x;
      s.y = pool.y;
      s.scale.set((pool.radius * 2) / tex.height);
      s.alpha = Math.min(0.85, pool.lifeSec / 1.2);
      s.tint = HIT_COLOR;
      s.visible = true;
    }
    hideFrom(this.catalystSprites, pools.length);
  }

  private syncArenaAbilities(world: World, playerX: number, playerY: number): void {
    const graphics = this.arenaAbilityGraphics;
    graphics.clear();
    if (!(world.abilities instanceof AbilitySystem)) {
      hideFrom(this.arenaAbilitySprites, 0);
      hideFrom(this.activeAbilitySprites, 0);
      hideFrom(this.arenaVisualSprites, 0);
      return;
    }

    let spriteCount = 0;
    for (const field of world.abilities.arenaFields) {
      const alpha = Math.min(1, field.lifeSec / Math.min(0.55, field.maxLifeSec));
      const age = field.maxLifeSec - field.lifeSec;
      if (field.kind === 'railSurge') {
        const x2 = field.x2 ?? field.x;
        const y2 = field.y2 ?? field.y;
        graphics.moveTo(field.x, field.y).lineTo(x2, y2).stroke({
          color: 0x74edff,
          width: field.radius * 2,
          alpha: 0.2 * alpha,
        });
        graphics.moveTo(field.x, field.y).lineTo(x2, y2).stroke({
          color: 0xd8ffff,
          width: 5,
          alpha: 0.9 * alpha,
        });
        continue;
      }

      if (field.kind === 'rootSnare') {
        graphics
          .circle(field.x, field.y, field.radius)
          .fill({ color: 0x4d8062, alpha: 0.12 * alpha })
          .stroke({ color: 0xa6f2dc, width: 2, alpha: 0.66 * alpha });
        for (let index = 0; index < 16; index++) {
          const angle = (index / 16) * Math.PI * 2;
          const distance = field.radius * (index % 2 === 0 ? 0.48 : 0.82);
          const texture = this.art.textures.statusRoot;
          const sprite = reuse(
            this.arenaAbilitySprites,
            this.arenaAbilityLayer,
            spriteCount++,
            texture,
          );
          sprite.texture = texture;
          sprite.x = field.x + Math.cos(angle) * distance;
          sprite.y = field.y + Math.sin(angle) * distance;
          const pulse = 1 + 0.18 * Math.sin(age * (10 - (index % 4)) + index * 0.7);
          setSpriteSize(sprite, 16 * pulse, 22 * pulse);
          sprite.rotation = angle - Math.PI / 2;
          sprite.tint = index % 3 === 0 ? 0xb9e84f : 0x6fae52;
          sprite.alpha = alpha;
          sprite.visible = true;
        }
      } else if (field.kind === 'nectarGrove') {
        graphics
          .circle(field.x, field.y, field.radius)
          .fill({ color: 0xffc35a, alpha: 0.13 * alpha })
          .stroke({ color: 0xffe48a, width: 3, alpha: 0.72 * alpha });
        const rotation = (age / 7.5) * Math.PI * 2;
        for (let index = 0; index < 12; index++) {
          const angle = (index / 12) * Math.PI * 2 + rotation;
          const texture =
            index % 3 === 0
              ? this.art.textures.projectileSap
              : this.art.textures.statusFirefly;
          const sprite = reuse(
            this.arenaAbilitySprites,
            this.arenaAbilityLayer,
            spriteCount++,
            texture,
          );
          sprite.texture = texture;
          sprite.x = field.x + Math.cos(angle) * field.radius * 0.72;
          sprite.y = field.y + Math.sin(angle) * field.radius * 0.72;
          setSpriteSize(sprite, 11, 9);
          sprite.rotation = angle;
          sprite.tint = index % 2 === 0 ? 0xffc35a : 0xfff1a6;
          sprite.alpha = alpha * (0.69 + 0.31 * Math.sin(age * 9 + index));
          sprite.visible = true;
        }
      } else if (field.kind === 'timeLock') {
        graphics
          .circle(field.x, field.y, field.radius)
          .fill({ color: 0x8bb5ff, alpha: 0.1 * alpha })
          .stroke({ color: 0xdce8ff, width: 3, alpha: 0.76 * alpha });
        const rotation = -(age / 6) * Math.PI * 2;
        for (let index = 0; index < 12; index++) {
          const angle = (index / 12) * Math.PI * 2 + rotation;
          const texture = this.art.textures.statusClock;
          const sprite = reuse(
            this.arenaAbilitySprites,
            this.arenaAbilityLayer,
            spriteCount++,
            texture,
          );
          sprite.texture = texture;
          sprite.x = field.x + Math.cos(angle) * field.radius * 0.82;
          sprite.y = field.y + Math.sin(angle) * field.radius * 0.82;
          setSpriteSize(sprite, 13, 13);
          sprite.rotation = rotation;
          sprite.tint = index % 2 === 0 ? 0xdce8ff : 0x8bb5ff;
          sprite.alpha = alpha;
          sprite.visible = true;
        }
      } else {
        graphics
          .circle(field.x, field.y, 30)
          .fill({ color: 0x05050a, alpha: 0.95 * alpha })
          .stroke({ color: 0x8bb5ff, width: 4, alpha: 0.9 * alpha });
        const rotation = -(age / 1.25) * Math.PI * 2;
        for (let index = 0; index < 14; index++) {
          const angle = (index / 14) * Math.PI * 2 + rotation;
          const distance = field.radius * (index % 3 === 0 ? 0.48 : 0.82);
          const texture = this.art.textures.abilityVoid;
          const sprite = reuse(
            this.arenaAbilitySprites,
            this.arenaAbilityLayer,
            spriteCount++,
            texture,
          );
          sprite.texture = texture;
          sprite.x = field.x + Math.cos(angle) * distance;
          sprite.y = field.y + Math.sin(angle) * distance;
          setSpriteSize(sprite, 11, 11);
          sprite.rotation = angle;
          sprite.tint = index % 2 === 0 ? 0x5965c8 : 0xb2cdff;
          sprite.alpha = alpha;
          sprite.visible = true;
        }
      }
    }

    for (const agent of world.abilities.arenaAgents) {
      const alpha = Math.min(1, agent.lifeSec / 0.4);
      const age = agent.maxLifeSec - agent.lifeSec;
      if (agent.kind === 'harvestSwarm') {
        const rotation = (age / 0.82) * Math.PI * 2;
        for (let index = 0; index < 8; index++) {
          const angle = (index / 8) * Math.PI * 2 + rotation;
          const radius = index % 2 === 0 ? 25 : 38;
          const texture = this.art.textures.statusFirefly;
          const sprite = reuse(
            this.arenaAbilitySprites,
            this.arenaAbilityLayer,
            spriteCount++,
            texture,
          );
          sprite.texture = texture;
          sprite.x = agent.x + Math.cos(angle) * radius;
          sprite.y = agent.y + Math.sin(angle) * radius;
          const pulse = 1 + 0.22 * Math.sin(age * 18 + index);
          setSpriteSize(sprite, 12 * pulse, 8 * pulse);
          sprite.rotation = angle;
          sprite.tint = index % 3 === 0 ? 0xfff4a8 : 0xffdf78;
          sprite.alpha = alpha;
          sprite.visible = true;
        }
      } else if (agent.kind === 'windStar') {
        const texture = this.art.textures.windShuriken;
        const star = reuse(
          this.arenaAbilitySprites,
          this.arenaAbilityLayer,
          spriteCount++,
          texture,
        );
        star.texture = texture;
        star.x = agent.x;
        star.y = agent.y;
        setSpriteSize(star, 46, 46);
        star.rotation = age * 9;
        star.tint = 0xcfeeff;
        star.alpha = alpha;
        star.visible = true;
      } else {
        const rotation = (age / 1.2) * Math.PI * 2;
        const bodyTexture = this.art.textures.statusSatellite;
        const body = reuse(
          this.arenaAbilitySprites,
          this.arenaAbilityLayer,
          spriteCount++,
          bodyTexture,
        );
        body.texture = bodyTexture;
        body.x = agent.x;
        body.y = agent.y;
        setSpriteSize(body, 30, 24);
        body.rotation = rotation;
        body.tint = 0xf260ce;
        body.alpha = alpha;
        body.visible = true;
        for (let index = 0; index < 4; index++) {
          const angle = (index / 4) * Math.PI * 2 + rotation;
          const texture = this.art.textures.abilityBolt;
          const bolt = reuse(
            this.arenaAbilitySprites,
            this.arenaAbilityLayer,
            spriteCount++,
            texture,
          );
          bolt.texture = texture;
          bolt.x = agent.x + Math.cos(angle) * 24;
          bolt.y = agent.y + Math.sin(angle) * 24;
          setSpriteSize(bolt, 8, 11);
          bolt.rotation = angle;
          bolt.tint = 0x74edff;
          bolt.alpha = alpha;
          bolt.visible = true;
        }
      }
    }
    hideFrom(this.arenaAbilitySprites, spriteCount);

    let activeCount = 0;
    let activeGroup = 0;
    for (let i = 0; i < world.abilityDefs.length; i++) {
      const definition = world.abilityDefs[i];
      if (world.abilities.active[i] <= 0 || definition.durationSec <= 0) continue;
      if (definition.key === 'eventHorizon') continue;
      const appearance = activeAbilityAppearance(definition.key, this.art);
      const radius = 25 + activeGroup * 7;
      activeGroup++;
      const groupAngle = (world.elapsedSec / appearance.spinDuration) * Math.PI * 2;
      for (let k = 0; k < appearance.count; k++) {
        const angle = groupAngle + (k / appearance.count) * Math.PI * 2;
        const sprite = reuse(
          this.activeAbilitySprites,
          this.orbitLayer,
          activeCount++,
          appearance.texture,
        );
        sprite.texture = appearance.texture;
        sprite.x = playerX + Math.cos(angle) * radius;
        sprite.y = playerY + Math.sin(angle) * radius;
        setSpriteSize(sprite, appearance.width, appearance.height);
        sprite.tint = definition.color;
        sprite.rotation = angle - Math.PI / 2;
        sprite.alpha = 0.71 + 0.29 * Math.sin(world.elapsedSec * 10 + k * 0.7);
        sprite.visible = true;
      }
    }
    hideFrom(this.activeAbilitySprites, activeCount);

    let visualCount = 0;
    for (const visual of world.abilities.arenaVisuals) {
      const age = visual.maxLifeSec - visual.lifeSec;
      const progress = clamp(age / visual.maxLifeSec, 0, 1);
      if (visual.kind === 'meteor') {
        const texture = this.art.textures.projectileComet;
        const sprite = reuse(
          this.arenaVisualSprites,
          this.beamLayer,
          visualCount++,
          texture,
        );
        sprite.texture = texture;
        sprite.x = visual.x - 150 * (1 - progress);
        sprite.y = visual.y - 260 * (1 - progress);
        setSpriteSize(sprite, 34 * (1 + progress * 0.35), 25 * (1 + progress * 0.35));
        sprite.rotation = 0.52;
        sprite.tint = visual.color;
        sprite.alpha = 1;
        sprite.visible = true;
        continue;
      }

      if (visual.kind === 'mycelialTendrils') {
        for (let spoke = 0; spoke < visual.count; spoke++) {
          const angle = visual.rotation + (spoke / visual.count) * Math.PI * 2;
          for (let segment = 1; segment <= 7; segment++) {
            const delay = segment * 0.018;
            const localProgress = clamp(
              (age - delay) / Math.max(0.01, visual.maxLifeSec - delay),
              0,
              1,
            );
            if (localProgress <= 0) continue;
            const texture = this.art.textures.statusRoot;
            const sprite = reuse(
              this.arenaVisualSprites,
              this.beamLayer,
              visualCount++,
              texture,
            );
            sprite.texture = texture;
            const distance = (segment / 7) * visual.radius;
            sprite.x = visual.x + Math.cos(angle) * distance;
            sprite.y = visual.y + Math.sin(angle) * distance;
            const scale = 1 + Math.sin(localProgress * Math.PI) * 0.35;
            setSpriteSize(sprite, 12 * scale, 17 * scale);
            sprite.rotation = angle - Math.PI / 2;
            sprite.tint = segment % 2 === 0 ? visual.color : (visual.alternateColor ?? visual.color);
            sprite.alpha = Math.sin(localProgress * Math.PI);
            sprite.visible = true;
          }
        }
        continue;
      }

      for (let index = 0; index < visual.count; index++) {
        const texture = this.art.textures[visual.texture] ?? this.art.textures.abilitySpark;
        const sprite = reuse(
          this.arenaVisualSprites,
          this.beamLayer,
          visualCount++,
          texture,
        );
        sprite.texture = texture;
        const baseAngle = (index / visual.count) * Math.PI * 2;
        const jitter = signedNoise(index + visual.x * 0.01 + visual.y * 0.02) * 0.04;
        const angle = baseAngle + jitter;
        const startRadius =
          visual.kind === 'localizedBurst'
            ? 0
            : 22 + unitNoise(index * 3.17 + visual.x * 0.03) * 14;
        const radiusVariation =
          visual.kind === 'localizedBurst'
            ? 1
            : 0.9 + unitNoise(index * 5.31 + visual.y * 0.02) * 0.16;
        const distance = lerp(startRadius, visual.radius * radiusVariation, progress);
        sprite.x = visual.x + Math.cos(angle) * distance;
        sprite.y = visual.y + Math.sin(angle) * distance;
        const scale =
          1 +
          progress *
            (visual.kind === 'flameWave'
              ? 0.25 + unitNoise(index * 1.83) * 0.45
              : 0.35);
        const aspect = visual.kind === 'flameWave' ? 1.28 : 1;
        setSpriteSize(sprite, visual.size * scale, visual.size * aspect * scale);
        sprite.rotation = angle - Math.PI / 2;
        sprite.tint =
          visual.alternateColor !== undefined && index % 3 === 0
            ? visual.alternateColor
            : visual.color;
        sprite.alpha =
          progress < 0.32 ? 1 : Math.max(0, (1 - progress) / 0.68);
        sprite.visible = true;
      }
    }
    hideFrom(this.arenaVisualSprites, visualCount);
  }

  private syncExpansionAura(world: World, playerX: number, playerY: number): void {
    const weaponKey = world.weapons.auraWeaponKey;
    const appearance = auraAppearance(weaponKey);
    const texture = this.art.textures[appearance.texture] ?? this.art.textures.auraCloud;
    const layout: ReadonlyArray<readonly [number, number, number, number]> = [
      [-0.62, -0.14, 0.42, 0],
      [-0.42, 0.42, 0.36, 0.1],
      [-0.18, -0.54, 0.38, 0.24],
      [0.02, 0.2, 0.48, 0.34],
      [0.26, -0.32, 0.42, 0.18],
      [0.54, 0.22, 0.4, 0.42],
      [0.66, -0.18, 0.34, 0.06],
      [-0.66, 0.28, 0.32, 0.3],
      [-0.28, 0.04, 0.46, 0.46],
      [0.38, 0.5, 0.34, 0.14],
      [0.12, -0.62, 0.32, 0.38],
      [0.7, 0.08, 0.28, 0.22],
    ];
    const rotation =
      weaponKey === 'gravityWell' ? -(world.elapsedSec / 2.2) * Math.PI * 2 : 0;
    for (let index = 0; index < layout.length; index++) {
      const [lx, ly, sizeScale, delay] = layout[index];
      const cos = Math.cos(rotation);
      const sin = Math.sin(rotation);
      const x = lx * cos - ly * sin;
      const y = lx * sin + ly * cos;
      const sprite = reuse(this.auraCloudSprites, this.auraLayer, index, texture);
      sprite.texture = texture;
      sprite.x = playerX + world.weapons.auraRadius * x;
      sprite.y = playerY + world.weapons.auraRadius * y;
      const pulse = 1 + 0.11 * Math.sin((world.elapsedSec + delay) * 7);
      const width =
        world.weapons.auraRadius * sizeScale * appearance.widthScale * pulse;
      setSpriteSize(sprite, width, width / appearance.aspect);
      sprite.tint =
        appearance.alternateColor !== undefined && index % 2 !== 0
          ? appearance.alternateColor
          : world.weapons.auraColor;
      sprite.alpha =
        appearance.alpha + 0.09 + 0.09 * Math.sin((world.elapsedSec + delay) * 6);
      sprite.rotation = rotation;
      sprite.visible = true;
    }
    hideFrom(this.auraCloudSprites, layout.length);
  }

  private syncBeamSymbols(beam: Beam, startIndex: number, alpha: number): number {
    const dx = beam.x2 - beam.x1;
    const dy = beam.y2 - beam.y1;
    const distance = Math.max(1, Math.hypot(dx, dy));
    const nx = -dy / distance;
    const ny = dx / distance;
    const progress = 1 - alpha;

    if (beam.kind === 'firefly') {
      const count = Math.max(4, Math.min(7, Math.floor(distance / 52)));
      for (let index = 0; index < count; index++) {
        const along = (index + 0.5) / count;
        const curve = Math.sin(along * Math.PI) * (index % 2 === 0 ? 12 : -12);
        const texture = this.art.textures.statusFirefly;
        const sprite = reuse(
          this.beamSymbolSprites,
          this.beamLayer,
          startIndex++,
          texture,
        );
        sprite.texture = texture;
        sprite.x = beam.x1 + dx * along + nx * (curve + progress * 4);
        sprite.y = beam.y1 + dy * along + ny * (curve + progress * 4);
        setSpriteSize(sprite, 12 * (1 + progress * 0.28), 8 * (1 + progress * 0.28));
        sprite.rotation = Math.atan2(dy, dx);
        sprite.tint = beam.color;
        sprite.alpha = alpha;
        sprite.visible = true;
      }
    } else if (beam.kind === 'spore') {
      for (let index = 1; index <= 5; index++) {
        const along = index / 6;
        const texture = this.art.textures.statusSpore;
        const sprite = reuse(
          this.beamSymbolSprites,
          this.beamLayer,
          startIndex++,
          texture,
        );
        sprite.texture = texture;
        sprite.x = beam.x1 + dx * along;
        sprite.y = beam.y1 + dy * along - progress * 7;
        setSpriteSize(sprite, 8, 8);
        sprite.rotation = 0;
        sprite.tint = index % 2 === 0 ? 0xd680d8 : 0x8ecb63;
        sprite.alpha = alpha;
        sprite.visible = true;
      }
    } else {
      for (let index = 1; index <= 12; index++) {
        const along = index / 13;
        const texture = this.art.textures.statusFlame;
        const sprite = reuse(
          this.beamSymbolSprites,
          this.beamLayer,
          startIndex++,
          texture,
        );
        sprite.texture = texture;
        sprite.x = beam.x1 + dx * along;
        sprite.y = beam.y1 + dy * along;
        setSpriteSize(sprite, 12 * (1 + progress * 0.4), 16 * (1 + progress * 0.4));
        sprite.rotation = Math.atan2(dy, dx) - Math.PI / 2;
        sprite.tint = index % 3 === 0 ? 0xffd34d : beam.color;
        sprite.alpha = alpha;
        sprite.visible = true;
      }
    }
    return startIndex;
  }

  /** Gravity rails read as temporary machinery assembled on the observatory floor:
   * paired metal runners, cross ties, end coils, and repeated directional chevrons. */
  private syncGravityRails(world: World): void {
    const rails = world.gravityRails;
    const g = this.railGraphics;
    g.clear();
    const pylon = this.art.textures.railPylon;
    const arrow = this.art.textures.railArrow;
    let pylonN = 0;
    let arrowN = 0;
    for (let i = 0; i < rails.length; i++) {
      const rail = rails[i];
      const half = rail.length * 0.5;
      const x1 = rail.x - rail.dx * half;
      const y1 = rail.y - rail.dy * half;
      const x2 = rail.x + rail.dx * half;
      const y2 = rail.y + rail.dy * half;
      const telegraph = rail.telegraphSec > 0;
      const pulse = telegraph ? 0.42 + 0.2 * Math.sin(rail.telegraphSec * 18) : 0.88;
      const nx = -rail.dy;
      const ny = rail.dx;
      const runnerOffset = rail.width * 0.31;

      if (!telegraph) {
        g.moveTo(x1, y1).lineTo(x2, y2).stroke({ color: 0x101416, width: rail.width, alpha: 0.34 });
      }
      for (const side of [-1, 1]) {
        const ox = nx * runnerOffset * side;
        const oy = ny * runnerOffset * side;
        g.moveTo(x1 + ox, y1 + oy)
          .lineTo(x2 + ox, y2 + oy)
          .stroke({ color: 0x293137, width: 6, alpha: telegraph ? 0.32 : 0.92 });
        g.moveTo(x1 + ox, y1 + oy)
          .lineTo(x2 + ox, y2 + oy)
          .stroke({
            color: telegraph ? 0x596065 : 0x8cf6ff,
            width: 2,
            alpha: telegraph ? pulse * 0.55 : 0.8,
          });
      }
      const tieCount = Math.max(4, Math.floor(rail.length / 30));
      for (let tie = 1; tie < tieCount; tie++) {
        const t = tie / tieCount - 0.5;
        const tx = rail.x + rail.dx * rail.length * t;
        const ty = rail.y + rail.dy * rail.length * t;
        const span = rail.width * 0.42;
        g.moveTo(tx - nx * span, ty - ny * span)
          .lineTo(tx + nx * span, ty + ny * span)
          .stroke({
            color: telegraph ? 0x75543b : 0xb1844d,
            width: 3,
            alpha: telegraph ? pulse * 0.38 : 0.72,
          });
      }
      for (let end = 0; end < 2; end++) {
        const s = reuse(this.railPylonSprites, this.railLayer, pylonN++, pylon);
        s.texture = pylon;
        s.x = end === 0 ? x1 : x2;
        s.y = end === 0 ? y1 : y2;
        s.scale.set(2.4);
        s.tint = telegraph ? 0x7d858a : 0xd0d5d5;
        s.alpha = telegraph ? 0.72 : 1;
        s.visible = true;
      }
      for (const t of [-0.24, 0, 0.24]) {
        const direction = reuse(this.railArrowSprites, this.railLayer, arrowN++, arrow);
        direction.texture = arrow;
        direction.x = rail.x + rail.dx * rail.length * t;
        direction.y = rail.y + rail.dy * rail.length * t;
        direction.rotation = Math.atan2(rail.dy, rail.dx) - Math.PI / 2;
        direction.scale.set(1.8);
        direction.tint = telegraph ? 0x8cf6ff : 0xe8ffff;
        direction.alpha = telegraph ? pulse * 0.75 : 0.9;
        direction.visible = true;
      }
    }
    hideFrom(this.railPylonSprites, pylonN);
    hideFrom(this.railArrowSprites, arrowN);
  }

  // Hidden-variant ability visuals: orbiting storks, the Back Hole marker sphere, and the
  // Trans Mute silence-note burst. Only called on /hidden.
  private syncHiddenFx(world: World, rx: number, ry: number): void {
    const hb = world.abilities as AbilitySystemHidden;

    // Berze Zeci: storks orbiting the player, each facing its travel direction.
    let storkN = 0;
    if (hb.storkActive) {
      storkN = hb.storkCount;
      const stex = this.art.textures.stork;
      const sc = 22 / stex.height;
      for (let k = 0; k < storkN; k++) {
        const a = hb.storkSpin + (k / storkN) * Math.PI * 2;
        const s = reuse(this.storkSprites, this.orbitLayer, k, stex);
        s.x = rx + Math.cos(a) * hb.storkRingRadius;
        s.y = ry + Math.sin(a) * hb.storkRingRadius;
        s.scale.set(sc);
        s.scale.x = sc * (Math.cos(a) >= 0 ? 1 : -1); // flip to face travel
        s.tint = HIT_COLOR;
        s.visible = true;
      }
    }
    hideFrom(this.storkSprites, storkN);

    // Back Hole: a dark pulsing sphere at the drop point.
    if (hb.bhActive) {
      this.bhSprite.visible = true;
      this.bhSprite.position.set(hb.bhX, hb.bhY);
      const pulse = 1 + 0.18 * Math.sin(world.elapsedSec * 10);
      this.bhSprite.scale.set(((28 * 2) / this.art.textures.disc.height) * pulse);
      this.bhSprite.tint = 0x2a1050;
      this.bhSprite.alpha = 0.9;
    } else {
      this.bhSprite.visible = false;
    }

    // Trans Mute: silence notes bursting outward + fading.
    let noteN = 0;
    if (hb.noteTimer > 0) {
      const t = hb.noteTimer / 0.5; // 1 -> 0
      noteN = 8;
      const spread = (1 - t) * 70 + 14;
      const ntex = this.art.textures.note;
      for (let k = 0; k < noteN; k++) {
        const a = (k / noteN) * Math.PI * 2;
        const s = reuse(this.noteSprites, this.particleLayer, k, ntex);
        s.x = hb.noteX + Math.cos(a) * spread;
        s.y = hb.noteY + Math.sin(a) * spread - (1 - t) * 10;
        s.scale.set(2.2);
        s.tint = 0xff4dff;
        s.alpha = t;
        s.visible = true;
      }
    }
    hideFrom(this.noteSprites, noteN);
  }

  // Two staggered falling droplets around (cx,cy) for a wet entity. Returns next sprite index.
  private emitDrips(dropN: number, cx: number, cy: number, spread: number, clock: number): number {
    for (let k = 0; k < 2; k++) {
      const ph = (clock * DRIP_RATE + k * 0.5) % 1;
      const dr = reuse(this.dropletSprites, this.dropletLayer, dropN++, this.art.textures.drop);
      dr.x = cx + (k === 0 ? -spread : spread);
      dr.y = cy + ph * DRIP_FALL;
      dr.scale.set(DROP_SCALE);
      dr.alpha = 1 - ph;
      dr.visible = true;
    }
    return dropN;
  }

  /** Set the world render zoom (lowered on phones to reveal more of the arena). */
  setZoom(z: number): void {
    this.zoom = z;
    this.worldLayer.scale.set(z);
  }

  private updateCamera(rx: number, ry: number, shakeTrauma: number): void {
    const sw = this.app.screen.width;
    const sh = this.app.screen.height;
    const camX = clampCenter(rx, sw / this.zoom / 2, ARENA.width);
    const camY = clampCenter(ry, sh / this.zoom / 2, ARENA.height);
    let ox = 0;
    let oy = 0;
    if (shakeTrauma > 0) {
      // Math.random is fine here (render-only, not the sim).
      const mag = shakeTrauma * shakeTrauma * MAX_SHAKE;
      ox = (Math.random() * 2 - 1) * mag;
      oy = (Math.random() * 2 - 1) * mag;
    }
    this.worldLayer.x = Math.round(sw / 2 - (camX - ox) * this.zoom);
    this.worldLayer.y = Math.round(sh / 2 - (camY - oy) * this.zoom);
  }
}

function activeAbilityAppearance(
  key: string,
  art: Art,
): {
  texture: Texture;
  width: number;
  height: number;
  count: number;
  spinDuration: number;
} {
  switch (key) {
    case 'thornskin':
      return { texture: art.textures.statusThorn, width: 10, height: 10, count: 7, spinDuration: 1.25 };
    case 'nectarWard':
      return { texture: art.textures.abilityShield, width: 10, height: 12, count: 5, spinDuration: 1.8 };
    case 'mycelialGrasp':
      return { texture: art.textures.statusRoot, width: 9, height: 12, count: 6, spinDuration: 1.6 };
    case 'fireflyRush':
    case 'harvestSwarm':
      return { texture: art.textures.statusFirefly, width: 12, height: 8, count: 7, spinDuration: 0.72 };
    case 'meteorMantle':
    case 'solarEruption':
      return { texture: art.textures.statusFlame, width: 9, height: 12, count: 7, spinDuration: 0.68 };
    case 'orbitGuard':
      return { texture: art.textures.statusSatellite, width: 13, height: 10, count: 5, spinDuration: 1.35 };
    case 'railSurge':
      return { texture: art.textures.statusRail, width: 14, height: 7, count: 6, spinDuration: 0.55 };
    case 'conduitStorm':
      return { texture: art.textures.abilityBolt, width: 9, height: 12, count: 7, spinDuration: 0.55 };
    // NinjaKid Dojo jutsu auras.
    case 'stoneJutsu':
      return { texture: art.textures.boulder, width: 10, height: 9, count: 7, spinDuration: 1.5 };
    case 'sexyJutsu':
      return { texture: art.textures.healthOrb, width: 10, height: 9, count: 6, spinDuration: 1.1 };
    case 'blueLightning':
      return { texture: art.textures.abilityBolt, width: 9, height: 12, count: 6, spinDuration: 0.5 };
    case 'ninjaDisc':
    case 'windStar':
      return { texture: art.textures.windShuriken, width: 11, height: 11, count: 5, spinDuration: 0.8 };
    case 'fireJutsu':
      return { texture: art.textures.statusFlame, width: 9, height: 12, count: 7, spinDuration: 0.68 };
    case 'spiritSmash':
      return { texture: art.textures.abilitySpark, width: 8, height: 8, count: 7, spinDuration: 0.6 };
    default:
      return { texture: art.textures.abilitySpark, width: 7, height: 7, count: 6, spinDuration: 1.25 };
  }
}

function auraAppearance(key: string): {
  texture: string;
  widthScale: number;
  aspect: number;
  alpha: number;
  alternateColor?: number;
} {
  switch (key) {
    case 'sporeShroud':
    case 'smolderMold':
      return {
        texture: 'statusSpore',
        widthScale: 0.34,
        aspect: 1,
        alpha: 0.62,
        alternateColor: 0x8ecb63,
      };
    case 'boilingWort':
    case 'coronaMist':
    case 'redGiant':
      return { texture: 'statusFlame', widthScale: 0.32, aspect: 0.78, alpha: 0.58 };
    case 'nectarCatalyst':
      return { texture: 'projectileSap', widthScale: 0.32, aspect: 1, alpha: 0.56 };
    case 'gravityWell':
      return { texture: 'abilityVoid', widthScale: 0.34, aspect: 1, alpha: 0.7 };
    default:
      return { texture: 'auraCloud', widthScale: 1, aspect: 1.47, alpha: 0.22 };
  }
}

function orbitWeaponAppearance(key: string): { texture: string; aspect: number } {
  switch (key) {
    case 'seedHalo':
      return { texture: 'orbitSeed', aspect: 1 };
    case 'thornCrown':
      return { texture: 'statusThorn', aspect: 1 };
    case 'fireflyHalo':
      return { texture: 'statusFirefly', aspect: 1.4 };
    case 'moonletArray':
      return { texture: 'statusSatellite', aspect: 1.28 };
    case 'cometCrown':
      return { texture: 'projectileComet', aspect: 1.28 };
    case 'orbitalStorm':
      return { texture: 'abilityBolt', aspect: 0.75 };
    default:
      return { texture: 'orb', aspect: 1 };
  }
}

function setSpriteSize(sprite: Sprite, width: number, height: number): void {
  sprite.scale.set(1);
  sprite.width = width;
  sprite.height = height;
}

function unitNoise(seed: number): number {
  const value = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return value - Math.floor(value);
}

function signedNoise(seed: number): number {
  return unitNoise(seed) * 2 - 1;
}

function drawStraightBeam(
  g: Graphics,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  color: number,
  alpha: number,
): void {
  g.moveTo(x1, y1)
    .lineTo(x2, y2)
    .stroke({ color, width: 7, alpha: alpha * 0.28 });
  g.moveTo(x1, y1)
    .lineTo(x2, y2)
    .stroke({ color, width: 3, alpha: alpha * 0.96 });
}

// Draw the SpriteKit-style jagged lightning path with a colored glow and white core.
function drawBolt(
  g: Graphics,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  color: number,
  alpha: number,
): void {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  const segs = Math.max(5, Math.min(12, Math.floor(len / 30)));
  const amplitude = Math.min(15, Math.max(6, len * 0.055));
  const points: Array<readonly [number, number]> = [[x1, y1]];
  for (let s = 1; s < segs; s++) {
    const t = s / segs;
    const taper = Math.sin(t * Math.PI);
    const jitter = signedNoise(x1 * 0.11 + y1 * 0.07 + x2 * 0.03 + s * 9.17);
    points.push([x1 + dx * t + nx * jitter * amplitude * taper, y1 + dy * t + ny * jitter * amplitude * taper]);
  }
  points.push([x2, y2]);

  const drawPath = (strokeColor: number, width: number, strokeAlpha: number): void => {
    g.moveTo(points[0][0], points[0][1]);
    for (let index = 1; index < points.length; index++) {
      g.lineTo(points[index][0], points[index][1]);
    }
    g.stroke({ color: strokeColor, width, alpha: strokeAlpha });
  };
  drawPath(color, 7, alpha * 0.32);
  drawPath(color, 3, alpha * 0.96);
  drawPath(0xffffff, 1, alpha * 0.92);

  for (let index = 3; index < points.length - 2; index += 4) {
    const [px, py] = points[index];
    const side = signedNoise(px * 0.13 + py * 0.17) >= 0 ? 1 : -1;
    const branchLength = 12 + unitNoise(px + py) * 12;
    g.moveTo(px, py)
      .lineTo(px + (dx / len) * branchLength * 0.35 + nx * branchLength * side * 0.45, py + (dy / len) * branchLength * 0.35 + ny * branchLength * side * 0.45)
      .lineTo(px + (dx / len) * branchLength * 0.7 + nx * branchLength * side, py + (dy / len) * branchLength * 0.7 + ny * branchLength * side)
      .stroke({ color, width: 2, alpha: alpha * 0.8 });
  }
}

// Scale a sprite so its texture height maps to the entity's diameter (2*radius).
function fit(s: Sprite, radius: number): void {
  s.scale.set((radius * 2) / s.texture.height);
}

// Reuse sprite at `idx`, creating it once with `tex` (lazy grow → stable).
function reuse(arr: Sprite[], layer: Container, idx: number, tex: Texture): Sprite {
  let s = arr[idx];
  if (s === undefined) {
    s = new Sprite(tex);
    s.anchor.set(0.5);
    layer.addChild(s);
    arr[idx] = s;
  }
  return s;
}

function hideFrom(sprites: Sprite[], from: number): void {
  for (let i = from; i < sprites.length; i++) sprites[i].visible = false;
}

function clampCenter(center: number, half: number, size: number): number {
  if (half * 2 >= size) return size / 2;
  return clamp(center, half, size - half);
}
